/**
 * Role-based access control, shared by client and server.
 *
 * A user's role lives in `auth.users.raw_app_meta_data.role` (hence in the JWT).
 * Code never asks "is this role X?": it asks `can(subject, permission)`, so adding
 * a role or changing what it grants only touches this file.
 */

export const ROLES = {
  admin: {
    label: "Admin",
    description: "Access to the back-office",
  },
  "super-admin": {
    label: "Super admin",
    description:
      "Access to the back-office, user role management and admin actions",
  },
} as const satisfies Record<string, { label: string; description: string }>;

export type Role = keyof typeof ROLES;

export const ROLE_KEYS = Object.keys(ROLES) as [Role, ...Role[]];

export const PERMISSIONS = [
  "backoffice:access",
  "users:assign-role",
  "admin-actions:run",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: ["backoffice:access"],
  "super-admin": [
    "backoffice:access",
    "users:assign-role",
    "admin-actions:run",
  ],
};

/** Whoever is being authorized. Teams will join the role here. */
export type AuthorizationSubject = { role: Role | null };

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && Object.hasOwn(ROLES, value);
}

/**
 * Reads the role from `app_metadata`. An unknown value yields no role, so a typo
 * or a removed role grants nothing.
 */
export function toRole(app_metadata?: Record<string, unknown>) {
  const role = app_metadata?.role;

  return isRole(role) ? role : null;
}

export function can(subject: AuthorizationSubject, permission: Permission) {
  return !!subject.role && ROLE_PERMISSIONS[subject.role].includes(permission);
}
